import React, { useState, useEffect, useRef } from "react";
import { FaUpload } from "react-icons/fa";
import axios from "axios";
import "../../css/DocumentUpload.css";
import { useSelector, useDispatch } from "react-redux";
import { TOAST_MESSAGES } from "../../utils/toastMessages";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import {
  addFiles,
  addUrls,
  addVideos,
  setStatus,
} from "../../redux/uploadQueueSlice";
import mammoth from "mammoth";
import api from "../../api";

import uploadIcon from "../../assets/UserIcons/UploadFileIcon.svg";
import crossIcon from "../../assets/UserIcons/Cancel.svg";
import linkIcon from "../../assets/UserIcons/Website.svg";
import videoIcon from "../../assets/UserIcons/VideoIcon.svg";
import fileIcon from "../../assets/UserIcons/FileIcon.svg";
import warningIcon from "../../assets/icons/File_upload_Icon.png";
import addIcon from "../../assets/UserIcons/CircleAddIcon.svg";
import ProcessingQueue from "../../users/ProcessingQueue";

const DocumentUpload = () => {
  const [files, setFiles] = useState([]);
  const [uploadMode, setUploadMode] = useState("Global");
  const { selectedModel } = useSelector((state) => state.model);
  const isFullPrivacy = false;
  const [showFirstModal, setShowFirstModal] = useState(false);
  const [urlLinks, setUrlLinks] = useState([""]);
  const [videoLinks, setVideoLinks] = useState([""]);
  const [activeUpload, setActiveUpload] = useState("files"); // "url" | "video"
  const [uploadStatus, setUploadStatus] = useState("uploading");
  const [uploadedInfo, setUploadedInfo] = useState({ type: "", count: 0 });
  const dispatch = useDispatch();
  const [totalPages, setTotalPages] = useState(0);
  const uploadState = useSelector((s) => s.uploadQueue);
  const [Description, setdescription] = useState("");

  const token = localStorage.getItem("access_token");

  // 🌐 Define endpoints for both modes
  const API_BASE = process.env.REACT_APP_API_URL;

  const getDocxPages = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    const text = result.value || "";
    const words = text.trim().split(/\s+/).length;

    return Math.ceil(words / 300);
  };

  const getTxtPages = async (file) => {
    const text = await file.text();
    const words = text.trim().split(/\s+/).length;

    return Math.ceil(words / 300);
  };

  const getImagePages = () => 1;

  const getFilePages = async (file) => {
    const fileType = file.type;

    // 1. PDF → exclude
    if (fileType === "application/pdf") {
      return 0;
    }

    // 2. DOCX
    if (
      fileType ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      return await getDocxPages(file);
    }

    // 3. TXT
    if (fileType === "text/plain") {
      return await getTxtPages(file);
    }

    // 4. Images
    if (fileType.startsWith("image/")) {
      return getImagePages();
    }

    // Default → count as 1
    return 1;
  };

  // Disappear when the input is focused
  const [focusedIndex, setFocusedIndex] = useState(null);

  const updateUserPreferences = async (payload) => {
    try {
      const res = await api.put(
        `${process.env.REACT_APP_API_URL}/user-profile/preferences`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.status === 200) {
        if (payload.theme_mode) {
          showSuccessToast(`Theme changed to ${payload.theme_mode}.`);
        } else if (payload.mode_type) {
          showSuccessToast(`Mode changed to ${payload.mode_type}.`);
        } else {
          // showSuccessToast("Preferences updated successfully.");
        }
      }
    } catch (error) {
      console.error("❌ Failed to update user preferences:", error);
      // showErrorToast("Failed to update preferences. Please try again.");
    }
  };

  // ✅ Remove single file
  const handleRemove = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // ✅ Handle drag & drop
  // const handleDrop = (e) => {
  //   e.preventDefault();
  //   const droppedFiles = Array.from(e.dataTransfer.files);
  //   setFiles((prev) => [...prev, ...droppedFiles]);
  // };

  // Allowed extensions
  const allowedExtensions = [
    "pdf",
    "docx",
    "txt",
    "doc",
    "pptx",
    "xlsx",
    "mp3",
    "wav",
    "aac",
    "flac",
    "mp4",
    "mov",
    "avi",
    "mkv",
    "flv",
    "png",
    "jpg",
    "jpeg",
    "gif",
    "bmp",
    "tiff",
  ];

  const filterSupportedFiles = (files) => {
    const supported = [];
    const unsupported = [];

    files.forEach((file) => {
      const ext = file.name.split(".").pop().toLowerCase();
      if (allowedExtensions.includes(ext)) {
        supported.push(file);
      } else {
        unsupported.push(file);
      }
    });

    return { supported, unsupported };
  };

  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);

    console.log("Selected:", selectedFiles);

    const { supported, unsupported } = filterSupportedFiles(selectedFiles);
    console.log("Supported:", supported);

    if (unsupported.length > 0) {
      const names = unsupported.map((f) => f.name).join(", ");
      showErrorToast(`Unsupported file(s): ${names}`);
    }

    if (supported.length === 0) return;

    let newTotal = totalPages;

    for (const file of supported) {
      const pages = await getFilePages(file);
      newTotal += pages;
    }

    setTotalPages(newTotal);
    setFiles((prev) => [...prev, ...supported]);

    // 🔥 THIS FIXES THE DOUBLE-TRIGGER PROBLEM
    setTimeout(() => {
      e.target.value = null;
    }, 0);
  };

  const handleDrop = async (e) => {
    e.preventDefault();

    const droppedFiles = Array.from(e.dataTransfer.files);

    const { supported, unsupported } = filterSupportedFiles(droppedFiles);

    if (unsupported.length > 0) {
      const names = unsupported.map((f) => f.name).join(", ");
      showErrorToast(`Unsupported file(s): ${names}`);
    }

    if (supported.length === 0) return;

    let newTotal = totalPages;

    for (const file of supported) {
      const pages = await getFilePages(file);
      newTotal += pages;
    }

    setTotalPages(newTotal);
    setFiles((prev) => [...prev, ...supported]);
  };

  const handleDragOver = (e) => e.preventDefault();

  //this will run onclick upload button
  const handleSubmit = () => {
    const isYouTube = (link) =>
      link.includes("youtube.com") || link.includes("youtu.be");

    const trimmedUrls = urlLinks.map((l) => l.trim());
    const trimmedVideos = videoLinks.map((l) => l.trim());

    // ---- VALIDATION FOR URL UPLOAD TAB ----
    if (activeUpload === "urls") {
      if (trimmedUrls.length === 0 || trimmedUrls.every((u) => u === "")) {
        showErrorToast("Please add at least one Web URL.");
        return;
      }
      if (trimmedUrls.some((link) => link === "")) {
        showErrorToast("Please fill all URL(s) fields before submitting.");
        setUploadStatus("error");
        return;
      }
    }

    // ---- VALIDATION FOR VIDEO UPLOAD TAB ----
    if (activeUpload === "videos") {
      if (trimmedVideos.length === 0 || trimmedVideos.every((v) => v === "")) {
        showErrorToast("Please add at least one Video URL(s).");
        return;
      }
      if (trimmedVideos.some((link) => link === "")) {
        showErrorToast(
          "Please fill all Video URL(s) fields before submitting."
        );
        setUploadStatus("error");
        return;
      }
    }

    // ---- SEPARATE DOCUMENTS, URLS, VIDEOS ----
    const validUrls = trimmedUrls.filter(
      (link) => link !== "" && !isYouTube(link)
    );

    const validVideos = trimmedVideos
      .concat(trimmedUrls.filter(isYouTube)) // YouTube URLs grouped as videos
      .filter((link) => link !== "");

    const hasFiles = files.length > 0;
    const hasUrls = validUrls.length > 0;
    const hasVideos = validVideos.length > 0;

    // ---- FINAL VALIDATION ----
    if (!hasFiles && !hasUrls && !hasVideos) {
      showErrorToast("Please select at least one Document");
      setUploadStatus("error");
      return;
    }

    // ---- SET UPLOADED INFO ----
    let uploadedInfo = {};
    if (hasFiles) uploadedInfo = { type: "Document", count: files.length };
    else if (hasUrls)
      uploadedInfo = { type: "Web URL", count: validUrls.length };
    else if (hasVideos)
      uploadedInfo = { type: "Video URL", count: validVideos.length };

    setUploadedInfo(uploadedInfo);

    // ---- OPEN MODAL ----
    setShowFirstModal(true);
  };

  const handleConfirm = async () => {
    // 🚫 BLOCK if something is already being processed
    if (
      uploadState.files.length > 0 || // files currently being uploaded
      uploadState.duplicates.length > 0 || // duplicate review open
      uploadState.status === "uploading" // queue processing
    ) {
      showErrorToast("Please wait. Current upload is still processing.");
      return;
    }
    try {
      setShowFirstModal(false);
      setUploadStatus("uploading");

      const user_id = localStorage.getItem("session_id");
      const token = localStorage.getItem("access_token");

      const headers = { Authorization: `Bearer ${token}` };
      const requests = [];

      const endpoints = {
        urls: `${API_BASE}/uploaded-files/upload_webpage`,
        videos: `${API_BASE}/uploaded-files/upload_youtube`,
      };

      const isYouTube = (link) =>
        link.includes("youtube.com") || link.includes("youtu.be");

      // CLEAN URL LISTS
      const validUrls = urlLinks.filter(
        (link) => link.trim() !== "" && !isYouTube(link)
      );

      const validVideos = videoLinks
        .concat(urlLinks.filter(isYouTube))
        .filter((link) => link.trim() !== "");

      const hasFiles = files.length > 0;
      const hasRealUrls = validUrls.length > 0;
      const hasRealVideos = validVideos.length > 0;

      // NOTHING SELECTED
      if (!hasFiles && !hasRealUrls && !hasRealVideos) {
        showErrorToast("Please select at least one file.");
        setUploadStatus("error");
        return;
      }

      // ------------------------------------------------------------
      // 0️⃣ PROCESSING STARTED (Only URLs / Videos)
      // ------------------------------------------------------------
      if (hasRealUrls) {
        showSuccessToast("Web URL(s) upload started.");
      }
      if (hasRealVideos) {
        showSuccessToast("Video URL(s) upload started.");
      }
      // ------------------------------------------------------------
      //  FILE UPLOAD → REDUX ONLY
      // ------------------------------------------------------------
      // ---- FILES → Add to Redux Queue
      if (hasFiles) {
        const jobId = Date.now() + Math.random();
        dispatch(
          addFiles({
            files,
            meta: {
              user_id,
              model: selectedModel,
              mode: uploadMode,
              isFullPrivacy,
              description: Description,
              jobId,
            },
          })
        );

        dispatch(setStatus("queued"));
        showSuccessToast("Added to upload queue");
        setdescription("");
        setFiles([]);
      }

      // ------------------------------------------------------------
      //  URL UPLOAD
      // ------------------------------------------------------------
      if (hasRealUrls) {
        const payload = {
          urls: validUrls,
          model: selectedModel,
          mode: uploadMode.toLowerCase(),
          deep_crawl: true,
          description: Description,
        };

        requests.push(
          axios.post(endpoints.urls, payload, {
            headers: { ...headers, "Content-Type": "application/json" },
          })
        );
        setFiles([]);
        setUrlLinks([""]);
        setVideoLinks([""]);
        setdescription("");
      }

      // ------------------------------------------------------------
      // VIDEO UPLOAD
      // ------------------------------------------------------------
      if (hasRealVideos) {
        const payload = {
          user_id,
          youtube_urls: validVideos,
          mode: uploadMode.toLowerCase(),
          model: selectedModel,
          description: Description,
        };

        requests.push(
          axios.post(endpoints.videos, payload, {
            headers,
          })
        );
        setFiles([]);
        setUrlLinks([""]);
        setVideoLinks([""]);
        setdescription("");
      }

      // ------------------------------------------------------------
      // RUN API REQUESTS
      // ------------------------------------------------------------

      if (requests.length > 0) {
        try {
          const responses = await Promise.all(requests);

          // All good → show success
          // showSuccessToast("Upload completed successfully!");

          // CLEAR INPUTS ONLY AFTER SUCCESS
          setFiles([]);
          setUrlLinks([""]);
          setVideoLinks([""]);
          setUploadStatus("success");
          if (hasRealUrls) {
            showSuccessToast("Web URL(s) upload successfully.");
          }
          if (hasRealVideos) {
            showSuccessToast("Video URL(s) upload successfully.");
          }
        } catch (err) {
          const backendMessage =
            err.response?.data?.message || "Something went wrong";

          showErrorToast(backendMessage);
          // if (hasRealUrls) {
          //   showErrorToast("Please enter valid web URL(s).");
          // }
          // if (hasRealVideos) {
          //   showErrorToast("Please enter valid video URL(s).");
          // }
        }
      }
    } catch (err) {
      console.error("❌ Upload error:", err);

      const backendMessage =
        err.response?.data?.message || "Something went wrong";

      showErrorToast(backendMessage);

      // if (hasRealUrls) {
      //   showErrorToast("Please enter valid web URL(s).");
      // }
      // if (hasRealVideos) {
      //   showErrorToast("Please enter valid video URL(s).");
      // }
      setUploadStatus("error");
    }
  };

  const getTitle = () => {
    switch (activeUpload) {
      case "urls":
        return "Upload Web URLs";
      case "videos":
        return "Upload Video URLs";
      default:
        return "Upload Documents";
    }
  };

  // ✅ Get icon
  const getIcon = () => {
    switch (activeUpload) {
      case "urls":
        return linkIcon;
      case "videos":
        return videoIcon;
      default:
        return fileIcon;
    }
  };

  const isValidUrl = (url) => {
    try {
      const parsed = new URL(url);
      // Optional: restrict to http or https only
      return parsed.protocol === "http:" || parsed.protocol === "https:";
    } catch (_) {
      return false;
    }
  };

  const renderUploadContent = () => {
    // const safeTrim = (str) => (typeof str === "string" ? str.trim() : "");
    // const lastUrl = safeTrim(urlLinks[urlLinks.length - 1]);
    // const lastVideo = safeTrim(videoLinks[videoLinks.length - 1]);

    // if web url come this if will work
    if (activeUpload === "urls") {
      // const showOuterBorder = urlLinks.some((link) => link.trim() !== "");

      return (
        <>
          <div className="w-100 text-center">
            {/* Outer Dashed Box */}
            <div
              // className={`rounded-4 p-3 p-md-5 ${
              //   showOuterBorder ? "border" : ""
              // }`}
              className={`rounded-4 p-3 p-md-5 border`}
              style={{
                borderWidth: "2px",
                borderStyle: "dashed",
                // borderColor: showOuterBorder ? "#ccc" : "transparent",
                borderColor: "#ccc",
                minHeight: "100px",
                transition: "all 0.3s ease",
              }}
            >
              {/* Inner Dashed Box */}
              <div
                className=" justify-content-center border-2 rounded-4 text-center position-relative p-3 p-md-5 overflow-hidden items-center"
                style={{
                  border: "1px dashed #ccc",
                  transition: "all 0.3s ease",
                }}
              >
                {urlLinks.map((link, index) => {
                  const value = link.trim();
                  const isInvalid = value !== "" && !isValidUrl(value);

                  return (
                    <div
                      key={index}
                      className="d-flex flex-column align-items-center gap-1 mb-3 w-100"
                    >
                      <div className="d-flex justify-content-center align-items-center gap-2 w-100">
                        {/* Input Field */}
                        <input
                          type="text"
                          className={`form-control ${
                            isInvalid ? "is-invalid" : ""
                          }`}
                          placeholder={
                            focusedIndex === index
                              ? ""
                              : "https://example.com/your-link"
                          }
                          value={link}
                          onFocus={() => setFocusedIndex(index)}
                          onBlur={() => setFocusedIndex(null)}
                          onChange={(e) => {
                            const newLinks = [...urlLinks];
                            newLinks[index] = e.target.value;
                            setUrlLinks(newLinks);
                          }}
                          required
                        />

                        {/* Delete Button */}
                        {urlLinks.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => {
                              const newLinks = urlLinks.filter(
                                (_, i) => i !== index
                              );
                              setUrlLinks(newLinks);
                            }}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Add More Button */}
                <div className="text-end mt-2">
                  <div
                    className="d-flex align-items-center gap-1 justify-content-end pointer"
                    disabled={urlLinks[urlLinks.length - 1]?.trim() === ""}
                    onClick={() => setUrlLinks([...urlLinks, ""])}
                  >
                    <span className="refresh-prompts">
                      <i className="bi bi-plus-circle"></i> Add More
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className="fw-semibold mb-1 d-flex align-items-center fs-6 fs-md-5 mt-4"
            style={{ color: "var(--dark-color)" }}
          >
            Description
          </div>

          <textarea
            className="form-control custom-textarea rounded-3 px-2 px-md-4 py-1 py-md-2"
            placeholder="Write here..."
            value={Description}
            onChange={(e) => setdescription(e.target.value)}
            style={{
              borderWidth: "1.5px",
              minHeight: "40px",
              resize: "vertical",
              fontSize: "14px",
              backgroundColor: "var(--background-color)",
              borderColor: "var(--border-color)",
              color: "var(--dark-color)",
            }}
          />
        </>
      );
    }
    // if youtube url come this if will work
    if (activeUpload === "videos") {
      const showOuterBorder = videoLinks.some((link) => link.trim() !== "");

      return (
        <>
          <div className="w-100 text-center">
            {/* Outer Dashed Box (only visible if any link has content) */}
            <div
              className={`rounded-4 p-3 p-md-5 border`}
              style={{
                borderWidth: "2px",
                borderStyle: "dashed",
                borderColor: "#ccc",
                minHeight: "100px",
                transition: "all 0.3s ease",
              }}
            >
              {/* Inner Dashed Box */}
              <div
                className="justify-content-center border-2 rounded-4 text-center position-relative p-3 p-md-5 overflow-hidden items-center"
                style={{
                  border: "1px dashed #ccc",
                  transition: "all 0.3s ease",
                }}
              >
                {videoLinks.map((link, index) => {
                  const value = link?.trim() || "";
                  const isInvalid = value !== "" && !isValidUrl(value);

                  return (
                    <div
                      key={index}
                      className="d-flex flex-column align-items-center gap-1 mb-3 w-100"
                    >
                      <div className="d-flex justify-content-center align-items-center gap-2 w-100">
                        {/* 🎥 Video Input */}
                        <input
                          type="text"
                          className={`form-control ${
                            isInvalid ? "is-invalid" : ""
                          }`}
                          placeholder={
                            focusedIndex === index
                              ? ""
                              : "https://example.com/your-link"
                          }
                          value={link || ""}
                          onFocus={() => setFocusedIndex(index)}
                          onBlur={() => setFocusedIndex(null)}
                          onChange={(e) => {
                            const newLinks = [...videoLinks];
                            newLinks[index] = e.target.value;
                            setVideoLinks(newLinks);
                          }}
                          required
                        />

                        {/* 🗑️ Delete Button */}
                        {videoLinks.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => {
                              const newLinks = videoLinks.filter(
                                (_, i) => i !== index
                              );
                              setVideoLinks(newLinks);
                            }}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* ➕ Add More Button */}
                <div className="text-end mt-2">
                  <div
                    className="d-flex align-items-center gap-1 justify-content-end"
                    style={{ cursor: "pointer" }}
                    onClick={() => setVideoLinks([...videoLinks, ""])}
                  >
                    <span className="refresh-prompts">
                      {" "}
                      <i className="bi bi-plus-circle "></i> Add More
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className="fw-semibold mb-1 d-flex align-items-center fs-6 fs-md-5 mt-4"
            style={{ color: "var(--dark-color)" }}
          >
            Description
          </div>

          <textarea
            className="form-control custom-textarea rounded-3 px-2 px-md-4 py-1 py-md-2"
            placeholder="Write here..."
            value={Description}
            onChange={(e) => setdescription(e.target.value)}
            style={{
              borderWidth: "1.5px",
              minHeight: "40px",
              resize: "vertical",
              fontSize: "14px",
              backgroundColor: "var(--background-color)",
              borderColor: "var(--border-color)",
              color: "var(--dark-color)",
            }}
          />
        </>
      );
    }

    // if document or file come this if will work
    return (
      <>
        <div
          // className={`rounded-4 p-3 p-md-5 ${files.length > 0 ? "border" : ""}`}
          className={`rounded-4 p-3 p-md-5 border`}
          style={{
            backgroundColor: "var(--background-color)",
            borderColor: "var(--border-color)",
            transition: "background-color 0.3s, border-color 0.3s",
          }}
        >
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="d-flex justify-content-center border-2 rounded-4 text-center position-relative p-3 p-md-5 overflow-hidden items-center"
            style={{
              borderStyle: "dashed",
              borderColor: "var(--border-color)",
              cursor: "pointer",
              minHeight: "300px",
              maxHeight: "80vh",
              backgroundColor: "var(--background-color)",
              color: "var(--dark-color)",
              transition:
                "background-color 0.3s, border-color 0.3s, color 0.3s",
            }}
          >
            <input
              type="file"
              id="fileInput"
              multiple
              // accept=".pdf,.docx,.txt,.doc,.pptx,.xlsx,.mp3,.wav,.aac,.flac,.mp4,.mov,.avi,.mkv,.flv,.png,.jpg,.jpeg,.gif,.bmp,.tiff"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />

            <div
              className="d-flex flex-column justify-content-center align-items-center w-100"
              onClick={() => document.getElementById("fileInput").click()}
            >
              <div className="d-flex flex-column justify-content-center align-items-center mb-1 mb-md-3">
                <div
                  className="rounded-circle border border-2 d-flex justify-content-center align-items-center p-2 p-md-3 mb-3"
                  style={{
                    width: "60px",
                    height: "60px",
                    borderStyle: "dashed",
                    border: "dashed var(--input-border-color)",
                    // borderColor: "var(--border-color)",
                    backgroundColor: "var(--bot-bubble-color)",
                  }}
                >
                  <img
                    src={uploadIcon}
                    alt="Upload"
                    className="icon-image1"
                    style={{
                      width: "40px ",
                      // maxHeight: "40px",

                      transition: "filter 0.3s ease",
                    }}
                  />
                </div>
                {/* <p
                  className="mb-1 fw-semibold  required-label fs-6 fs-md-5"
                  style={{ color: "var(--dark-color)" }}
                >
                  Drag & Drop File or
                </p> */}

                <p className="mb-1 fw-semibold">Drag and Drop here</p>
                <p className=" fw-semibold mb-1">or</p>
                <button
                  type="button"
                  className=" bg-secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    document.getElementById("fileInput").click();
                  }}
                  style={{
                    marginTop: "8px",
                    padding: "8px 16px",
                    // backgroundColor: isAdded ? "#cccccc" : "var(--secondary-color)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <i className="bi bi-folder2-open" />
                  Browse Files
                </button>
                {/*  <p
                  className="small mt-2 supported-text"
                  style={{ color: "var(--dark-color)" }}
                >
            Supported: .pdf, .doc, .docx, .txt, .mp3, .wav,.mp4,
                  .avi, .mov, .mkv, .png, .jpg, .jpeg, .ppt, .pptx 
              
                </p>*/}
              </div>

              {files.length > 0 && (
                <div
                  className="w-100 px-1 px-md-5 pb-1 pb-md-3"
                  style={{
                    minHeight: "55px",
                    maxHeight: "175px",
                    overflowY: "auto",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="row g-2 justify-content-center">
                    {files.map((file, index) => (
                      <div
                        key={index}
                        className="col-6 col-sm-4 col-md-4 col-lg-4"
                      >
                        <div
                          className="border rounded px-2 py-2 d-flex align-items-center shadow-sm"
                          style={{
                            width: "100%",
                            backgroundColor: "var(--user-bubble-color)",
                            color: "var(--dark-color)",
                            borderColor: "var(--border-color)",
                            padding: "8px",
                          }}
                        >
                          <i
                            className="bi bi-file-earmark refresh-prompts me-2"
                            // style={{ color: "var(--secondary-color)" }}
                          ></i>
                          <span
                            className="text-truncate flex-grow-1"
                            title={file.name}
                          >
                            {file.name}
                          </span>
                          <button
                            type="button"
                            className="btn-close icon-image btn-sm ms-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemove(index);
                            }}
                            // style={{
                            //   filter: "invert(var(--btn-close-invert, 0))",
                            // }}
                          ></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div
          className="fw-semibold mb-1 d-flex align-items-center fs-6 fs-md-5 mt-4"
          style={{ color: "var(--dark-color)" }}
        >
          Description
        </div>

        <textarea
          className="form-control custom-textarea rounded-3 px-2 px-md-4 py-1 py-md-2"
          placeholder="Write here..."
          value={Description}
          onChange={(e) => setdescription(e.target.value)}
          style={{
            borderWidth: "1.5px",
            minHeight: "40px",
            resize: "vertical",
            fontSize: "14px",
            backgroundColor: "var(--background-color)",
            borderColor: "var(--border-color)",
            color: "var(--dark-color)",
          }}
        />
      </>
    );
  };

  return (
    <div className="container-fluid px-4 position-relative train-container">
      {/* Main Title */}
      <div
        className="align-items-center mb-3"
        style={{
          color: "var(--dark-color)",
          transition: "color 0.3s ease",
        }}
      >
        <h5
          className="fw-semibold mb-0 d-flex align-items-center fs-6 fs-md-4"
          style={{ color: "var(--dark-color)" }}
        >
          <img
            src={getIcon()}
            alt={getTitle()}
            className="me-2 theme-icon"
            style={{
              width: 20,
              height: 20,
              objectFit: "contain",
              transition: "filter 0.3s ease",
            }}
          />
          {getTitle()}
        </h5>
      </div>

      {/* Tabs to switch */}
      <div className="gap-md-2 d-flex justify-content-center justify-content-md-end mb-1 mb-md-3 upload-options-wrapper">
        {activeUpload !== "urls" && (
          <>
            <span
              className="upload-option"
              onClick={() => {
                setActiveUpload("urls");
                setFiles([]);
                setUrlLinks([""]);
                setVideoLinks([""]);
                setdescription("");
              }}
            >
              <i className="bi bi-link-45deg me-1"></i> Upload Web URLs
            </span>
            <span className="separator">|</span>
          </>
        )}

        {activeUpload !== "videos" && (
          <>
            <span
              className="upload-option"
              onClick={() => {
                setActiveUpload("videos");
                setFiles([]);
                setUrlLinks([""]);
                setVideoLinks([""]);
                setdescription("");
              }}
            >
              <i className="bi bi-camera-video me-1"></i> Upload Video URLs
            </span>
            <span className="separator">|</span>
          </>
        )}

        {activeUpload !== "files" && (
          <>
            <span
              className="upload-option"
              onClick={() => {
                setActiveUpload("files");
                setFiles([]);
                setUrlLinks([""]);
                setVideoLinks([""]);
                setdescription("");
              }}
            >
              <i className="bi bi-file-earmark-text me-1"></i> Upload Documents
            </span>
            <span className="separator">|</span>
          </>
        )}
      </div>

      {/* Dynamic Upload Box */}
      {renderUploadContent()}

      {showFirstModal && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="document-modal modal-content text-center">
              {/* Header */}
              <div className="modal-header justify-content-center position-relative">
                <button
                  type="button"
                  className="btn position-absolute "
                  aria-label="Close"
                  style={{
                    top: "10px",
                    right: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 9999,
                    padding: 0,
                    border: "none",
                  }}
                >
                  <img
                    src={crossIcon}
                    alt="Close"
                    onClick={() => setShowFirstModal(false)} // click only triggers when image is clicked
                    style={{
                      width: "20px",
                      height: "20px",
                    }}
                    className="icon-image"
                  />
                </button>
              </div>

              {/* Body */}
              <div className="modal-body" style={{ padding: "20px" }}>
                {/* ✅ Centered Icon */}
                <div className="d-flex justify-content-center align-items-center mb-3">
                  <div
                    className="rounded-circle d-flex justify-content-center align-items-center p-3"
                    style={{
                      width: "60px",
                      height: "60px",
                      backgroundColor: "#E3E6EA",
                    }}
                  >
                    <img
                      src={fileIcon}
                      alt="Upload"
                      style={{ maxWidth: "30px", maxHeight: "30px" }}
                    />
                  </div>
                </div>

                <p className="refresh-prompts">
                  You have uploaded {uploadedInfo.count} {uploadedInfo.type}(s).
                  You are currently in <>{uploadMode} Mode</>.
                </p>

                <p className="refresh-prompts">
                  You can change the mode below if needed:
                </p>

                <div className="d-flex justify-content-center md:gap-4 my-3">
                  {["Global", "Personal"].map((mode) => (
                    <label
                      key={mode}
                      className="form-check custom-form-check d-flex align-items-center gap-2 pl-0 p-2"
                      style={{ cursor: "pointer" }}
                    >
                      <input
                        type="radio"
                        name="mode"
                        className="custom-radio"
                        value={mode}
                        checked={uploadMode === mode}
                        onChange={() => setUploadMode(mode)}
                      />
                      <span className="refresh-prompts">{mode}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className="modal-footer justify-content-center mb-3 gap-3">
                {/* <button
                  className="cancle-btn w-25"
                 
                >
                  Cancel
                </button> */}

                <button
                  type="button"
                  className="cancle-btn w-25  border border-1 fw-semibold"
                  // style={{ borderColor: "#3e3e3e" }}
                  onClick={() => {
                    setShowFirstModal(false);
                  }}
                  // disabled={isCancelling}
                >
                  Cancel
                </button>
                <button className="custom-btn w-25" onClick={handleConfirm}>
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(files.length > 0 ||
        urlLinks.some((u) => u.trim() !== "") ||
        videoLinks.some((v) => v.trim() !== "")) && (
        <div className="d-flex flex-wrap justify-content-center gap-3 mt-2 mt-md-4">
          {/* 🗑️ Deselect All */}
          <button
            className="cancle-btn px-5"
            onClick={() => {
              setFiles([]);
              setUrlLinks([""]);
              setVideoLinks([""]);
              setdescription("");
            }}
          >
            <span className="refresh-prompts">Cancel</span>
          </button>

          {(() => {
            const totalUploads =
              files.length +
              urlLinks.filter((u) => u.trim() !== "").length +
              videoLinks.filter((v) => v.trim() !== "").length;

            // Decide button text
            const buttonText = totalUploads > 1 ? "Upload All" : "Upload";

            // Enable only when something is selected
            const isActive = totalUploads > 0;

            return (
              <button
                className={` px-5 custom-btn`}
                onClick={handleSubmit}
                // disabled={!isActive}
              >
                {buttonText}
              </button>
            );
          })()}
        </div>
      )}

      {activeUpload === "files" && (
        <>
          <ProcessingQueue />
        </>
      )}
    </div>
  );
};

export default DocumentUpload;
